import { useLocalStorage } from '@/hooks/useLocalStorage';
import { personas, type PersonaSlug } from '@/lib/personas';

const STORAGE_KEY = 'aievals:roles';

/**
 * Site-wide "show my role" preference. An empty list means All roles (no
 * filter), which is the default. Shared by the command palette and the
 * listing pages so a visitor sets their role once and every list narrows
 * to their slice.
 */
export function useRoleFilter() {
  return useLocalStorage<PersonaSlug[]>(STORAGE_KEY, []);
}

/**
 * A listing item matches when no role is selected, or when its personas
 * intersect the selection (OR across selected roles).
 */
export function matchesRoles(
  itemPersonas: string[] | undefined,
  selected: PersonaSlug[],
): boolean {
  if (selected.length === 0) return true;
  if (!itemPersonas || itemPersonas.length === 0) return false;
  return itemPersonas.some((p) => (selected as string[]).includes(p));
}

/**
 * The roles that actually tag at least one item in a list. Listing pages
 * offer only these as chips, so a reader never selects a role (e.g. one the
 * content has not been tagged for yet) that would empty the page.
 */
export function availableRoles(
  items: { personas?: string[] }[],
): PersonaSlug[] {
  return personas
    .filter((p) => items.some((it) => (it.personas ?? []).includes(p.slug)))
    .map((p) => p.slug);
}
